import { Channel } from "redux-saga";
import { actionChannel, all, call, delay, flush, fork, put, select, take, takeEvery } from "redux-saga/effects";

import { bridge, createLoadLocalizationItemsAction, ILocalizationItemsLoadedAction } from "@multiweb/reactCore/bridge";
import { createSetFetchingAction } from "@multiweb/reactCore/fetching";
import { SagaWithReturnType, SagaYieldReturn } from "@multiweb/types/react";
import {
    createClearLocalizationItemsAction,
    createConcatLocalizationItemsAction,
    createLoadLocalizationKeysAction,
    createLocalizationKeysLoadedAction,
    createSetLocaleCodeAction,
    createSetSupportedLanguagesAction,
} from "./actions";
import { BATCHING_DELAY, BATCHING_FETCH_KEY, RELOAD_FETCH_KEY } from "./constants";
import { logger } from "./logger";
import { getLocaleCode, getLocalizationKeys } from "./selectors";
import { ILoadLocalizationKeysAction } from "./types";
import {
    createAreLocalizationItemsLoadedActionPattern,
    createLoadLocalizationKeysActionPattern,
    createSetLocaleCodeActionPattern,
    mergeLoadLocalizationKeysActions,
    transformLoadedLocalizationItems,
} from "./utils";

const { actualCultureISO639_1, getSupportedLanguages } = bridge;

export const saga: SagaWithReturnType = function* i18nSaga() {
    yield fork(loadLocaleCodeSaga);
    yield fork(loadSupportedLanguagesSaga);
    yield fork(batchingLoadLocalizationKeysSaga);
    yield fork(watchLocaleChangeSaga);
    yield takeEvery(createLoadLocalizationKeysActionPattern(false), loadLocalizationKeysSaga);
};

/**
 * Obsluhuje požadavky na načtení dat pro lokalizační klíče s požadovaným batchováním. Po prvním požadavku čeká po dobu stanovenou
 * v {@link BATCHING_DELAY} na příchod dalších požadavků. Poté spojí všechny požadavky dohromady a obslouží je pomocí standardní ságy pro načítání
 * {@link loadLocalizationKeysSaga}.
 *
 * Pro čekání na výsledky využívá interní načítací klíč {@link BATCHING_FETCH_KEY}. Po dobu načítání však nastaví fetching pro všechny fetching klíče,
 * které se nacházely ve spojených akcích.
 */
export const batchingLoadLocalizationKeysSaga: SagaWithReturnType = function* batchingLoadLocalizationKeysSaga() {
    const loadLocalizationsKeysActionChannel: Channel<ILoadLocalizationKeysAction> = yield actionChannel(createLoadLocalizationKeysActionPattern(true));
    logger.debug("Localization saga for action batching is initialized.");
    while (true) {
        const firstAction: ILoadLocalizationKeysAction = yield take(loadLocalizationsKeysActionChannel);
        yield delay(BATCHING_DELAY);
        const bufferedActions: ReadonlyArray<ILoadLocalizationKeysAction> = yield flush(loadLocalizationsKeysActionChannel);
        const allActions = [firstAction, ...bufferedActions];
        const mergedActionsData: ReturnType<typeof mergeLoadLocalizationKeysActions> = yield call(mergeLoadLocalizationKeysActions, allActions);
        logger.debug(() => `Batched ${allActions.length} actions after ${BATCHING_DELAY}ms. Aggregated actions data: ${JSON.stringify(mergedActionsData)}`);
        yield all(mergedActionsData.fetchingKeys.map((fetchingKey) =>
            put(createSetFetchingAction(fetchingKey, true))));
        yield call(loadLocalizationKeysSaga, createLoadLocalizationKeysAction(mergedActionsData.locKeys, BATCHING_FETCH_KEY));
        yield all(mergedActionsData.fetchingKeys.map((fetchingKey) =>
            put(createSetFetchingAction(fetchingKey, false))));
    }
};

/**
 * Obsluhuje požadavky na načtení dat pro lokalizační klíče (bez batchování). Načtené lokalizační položky doplní k ostatním položkám ve stavu.
 */
export const loadLocalizationKeysSaga: SagaWithReturnType<[ILoadLocalizationKeysAction]> = function* loadLocalizationKeysSaga(action) {
    const { fetchingKey, localizationKeys } = action.payload;
    yield put(createSetFetchingAction(fetchingKey, true));
    yield put(createLoadLocalizationItemsAction(localizationKeys, fetchingKey));

    const localizationItemsLoadedAction: ILocalizationItemsLoadedAction = yield take(createAreLocalizationItemsLoadedActionPattern(fetchingKey));
    const { localizationItems } = localizationItemsLoadedAction.payload;
    const transformedLocalizationItems: ReturnType<typeof transformLoadedLocalizationItems> = yield call(transformLoadedLocalizationItems, localizationKeys, localizationItems);
    yield put(createConcatLocalizationItemsAction(transformedLocalizationItems));

    yield put(createSetFetchingAction(fetchingKey, false));
    yield put(createLocalizationKeysLoadedAction(fetchingKey));
};

/**
 * Obsluhuje načtení aktuálního kódu jazyka aplikace
 */
export const loadLocaleCodeSaga: SagaWithReturnType = function* loadLocaleCodeSaga() {
    yield put(createSetLocaleCodeAction(actualCultureISO639_1));
};

/**
 * Obsluhuje načtení podporovaných jazyků aplikace
 */
export const loadSupportedLanguagesSaga: SagaWithReturnType = function* loadSupportedLanguagesSaga() {
    const supportedLanguages: SagaYieldReturn<typeof getSupportedLanguages> = yield call(getSupportedLanguages);
    yield put(createSetSupportedLanguagesAction(supportedLanguages));
};

/**
 * Sleduje změny jazyka a spouští přenačtení lokalizací
 */
export const watchLocaleChangeSaga: SagaWithReturnType = function* watchLocaleChangeSaga() {
    while (true) {
        const currentLocaleCode = yield select(getLocaleCode);
        yield take(createSetLocaleCodeActionPattern(currentLocaleCode));
        const localizationKeys = yield select(getLocalizationKeys);
        yield put(createClearLocalizationItemsAction());
        yield call(loadLocalizationKeysSaga, createLoadLocalizationKeysAction(localizationKeys, RELOAD_FETCH_KEY));
    }
};
